<template>
  <div class="validation-section animate-in delay-7">
    <button class="validation-toggle" @click="open = !open" :aria-expanded="open">
      <div class="validation-toggle-left">
        <div :class="['validation-toggle-icon', hasWarnings ? 'warn' : 'success']">
          <i :class="`bi-${hasWarnings ? 'exclamation' : 'check'}-circle`" />
        </div>
        <span class="validation-toggle-label">Data Quality</span>
        <span :class="['validation-toggle-count', { warn: hasWarnings || error, success: metrics && !loading && !error && !hasWarnings }]">
          {{ loading ? 'Loading…' : error || !metrics ? 'Unavailable' : hasWarnings ? `${formatCount(warningCount, 'warning')}` : 'Looking good' }}
        </span>
      </div>
      <div class="validation-toggle-line"></div>
      <div :class="['validation-toggle-chevron', {open}]">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
    </button>
    <BCollapse :show="open" disable-auto-scroll-on-show>
      <p v-if="loading" class="small text-muted mt-3" role="status">Loading data quality checks…</p>
      <p v-else-if="error || !metrics" class="small text-muted mt-3" role="status">Data quality checks could not be loaded. <button type="button" class="btn btn-sm btn-outline-secondary" @click="$emit('retry')">Retry</button></p>
      <div v-else class="validation-body">
        <div v-for="tile in tiles" :key="tile.title" class="validation-item">
          <div :class="['validation-item-icon', tile.variant]">
            <i :class="`bi-${tile.icon}`"/>
          </div>
          <div>
            <div class="validation-item-title">
              {{ tile.title }}
            </div>
            <div class="validation-item-detail">
              <template v-if="tile.value">
                <span :class="['validation-item-count', tile.variant]">
                  {{ formatNumber(tile.value) }}
                </span> {{ tile.detail }}
              </template>
              <template v-else>
                {{ tile.clearDetail }}
              </template>
            </div>
          </div>
        </div>
      </div>
    </BCollapse>
  </div>
</template>

<script>
import BCollapse from '@/components/common/BCollapse.vue';
export default {
  components: { BCollapse },
  emits: ['retry'],
  props: {
    metrics: {
      type: Object,
      default: null
    },
    loading: Boolean,
    error: { type: String, default: null }
  },
  data() {
    return {
      open: false
    };
  },
  computed: {
    hasWarnings() {
      return this.warningCount > 0;
    },
    warningCount() {
      return this.tiles.filter(tile => tile.value > 0).length;
    },
    tiles() {
      const m = this.metrics;
      if (!m) {
        return [];
      }

      const missingLatLng = m.properties?.validation?.noLatLng || 0;
      const missingPropertyId = m.licenses?.validation?.noPropertyId || 0;
      const missingPropertySnap = m.licenses?.validation?.noPropertySnapshot || 0;
      const missingIssuedOn = m.licenses?.validation?.noIssuedOn || 0;
      const missingExpiresOn = m.licenses?.validation?.noExpiresOn || 0;
      return [
        {
          title: 'Property Geolocation',
          variant: missingLatLng > 0 ? 'warn' : 'success',
          icon: 'geo-alt',
          value: missingLatLng,
          detail: `propert${missingLatLng === 1 ? 'y' : 'ies'} have no lat/lng coordinates`,
          clearDetail: 'All properties have lat/lng coordinates'
        },
        {
          title: 'License Property IDs',
          variant: missingPropertyId > 0 ? 'warn' : 'success',
          icon: 'file-earmark-check',
          value: missingPropertyId,
          detail: `license${missingPropertyId === 1 ? '' : 's'} have an invalid or missing parcel ID`,
          clearDetail: 'All licenses have valid parcel IDs'
        },
        {
          title: 'License Property Snapshots',
          variant: missingPropertySnap > 0 ? 'warn' : 'success',
          icon: 'file-earmark-richtext',
          value: missingPropertySnap,
          detail: `license${missingPropertySnap === 1 ? '' : 's'} have no cached property snapshot`,
          clearDetail: 'All licenses have up-to-date property snapshots'
        },
        {
          title: 'License Issued Dates',
          variant: missingIssuedOn > 0 ? 'warn' : 'success',
          icon: 'calendar-check',
          value: missingIssuedOn,
          detail: `active license${missingIssuedOn === 1 ? '' : 's'} missing an issued date`,
          clearDetail: 'All active licenses have valid issued dates'
        },
        {
          title: 'License Expiration Dates',
          variant: missingExpiresOn > 0 ? 'warn' : 'success',
          icon: 'calendar-x',
          value: missingExpiresOn,
          detail: `active license${missingExpiresOn === 1 ? '' : 's'} missing an expiration date`,
          clearDetail: 'All active licenses have valid expiration dates'
        },
      ];
    }
  }
}
</script>

<style scoped>
.validation-section {
  margin-top: 40px;
}

.validation-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 0;
  background: none;
  border: none;
  font-family: inherit;
  width: 100%;
}

.validation-toggle-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.validation-toggle-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.validation-toggle-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary);
}

.validation-toggle-count {
  font-size: 11px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  padding: 1px 7px;
  border-radius: 10px;
}

.validation-toggle-count.warn {
  background: var(--amber-bg);
}
.validation-toggle-icon.warn, .validation-toggle-count.warn {
  color: var(--amber);
}

.validation-toggle-count.success {
  background: var(--green-bg);
}
.validation-toggle-icon.success, .validation-toggle-count.success {
  color: var(--green);
}

.validation-toggle-line {
  flex: 1;
  height: 1px;
  border-top: 1px dashed var(--border);
}

.validation-toggle-chevron {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
  transition: transform 0.2s ease;
}

.validation-toggle-chevron.open {
  transform: rotate(180deg);
}

.validation-body {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.validation-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: var(--surface);
  border: 1px dashed var(--border);
  border-radius: var(--radius-sm);
  transition: border-color 0.15s ease;
}

.validation-item:hover {
  border-color: var(--border-hover);
}

.validation-item-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.validation-item-icon.warn {
  background: var(--amber-bg);
  color: var(--amber);
}

.validation-item-icon.error {
  background: var(--red-bg);
  color: var(--red);
}

.validation-item-icon.info {
  background: var(--blue-bg);
  color: var(--blue);
}

.validation-item-icon.success {
  background: var(--green-bg);
  color: var(--green);
}

.validation-item-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.validation-item-detail {
  font-size: 12px;
  color: var(--text-tertiary);
  line-height: 1.4;
}

.validation-item-count {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  font-size: 12px;
}

.validation-item-count.warn { color: var(--amber); }
.validation-item-count.error { color: var(--red); }
.validation-item-count.info { color: var(--blue); }
.validation-item-count.success { color: var(--green); }

</style>
